import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer, AlertTriangle } from "lucide-react";
import * as api from "../lib/api";
import { buttonVariants } from "../components/ui/button";
import type { BokSmartFormData } from "@dugads/shared";

// 154 -- DRAFT BokSmart team sheet. Assembles squad (with 153 backup front-row
// flags), coach BokSmart numbers (148), and match officials. The exact official
// SA Rugby layout is pending Gustav's template; this print view is clearly
// marked DRAFT and prints to PDF via the browser.
export default function BokSmartFormPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const [data, setData] = useState<BokSmartFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    try {
      setData(await api.getBokSmartForm(matchId));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load BokSmart form");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className="text-muted-foreground" data-page="boksmart-form" data-state="loading">Loading...</p>;
  }
  if (error || !data) {
    return (
      <div className="flex flex-col gap-4" data-page="boksmart-form" data-state="error">
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Link to={`/matches/${matchId}`} className={buttonVariants({ variant: "outline", size: "sm" })}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Match
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-page="boksmart-form" data-state="ready">
      {/* Toolbar -- hidden when printing */}
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Link to={`/matches/${matchId}`} className={buttonVariants({ variant: "outline", size: "sm" })}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className={buttonVariants({ variant: "default", size: "sm" })}
          data-action="print-boksmart"
        >
          <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Draft notice */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#FAEEDA] bg-[#FAEEDA] px-3 py-2 text-[#633806]">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p className="text-xs">{data.draft_notice}</p>
      </div>

      <div className="rounded-lg border border-border bg-background p-6">
        <h1 className="text-2xl font-semibold">BokSmart Team Sheet (Draft)</h1>
        <p className="mt-1 text-sm text-muted-foreground">{data.match_name}</p>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div><dt className="inline text-muted-foreground">Kickoff: </dt><dd className="inline">{new Date(data.starts_at).toLocaleString()}</dd></div>
          {data.age_group && <div><dt className="inline text-muted-foreground">Age group: </dt><dd className="inline">{data.age_group}</dd></div>}
          {data.venue && <div><dt className="inline text-muted-foreground">Venue: </dt><dd className="inline">{data.venue}</dd></div>}
        </dl>

        {/* Officials */}
        <section className="mt-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Match officials</h2>
          {data.officials.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">No officials captured.</p>
          ) : (
            <ul className="mt-1 text-sm">
              {data.officials.map((o) => (
                <li key={o.id}>
                  <span className="font-medium">{o.role === "referee" ? "Referee" : "Assistant referee"}:</span>{" "}
                  {o.name}{o.boksmart_number ? ` (BokSmart ${o.boksmart_number})` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Teams */}
        {data.teams.map((t) => (
          <section key={t.team_id} className="mt-6">
            <h2 className="text-base font-semibold">{t.team_name} <span className="text-xs font-normal text-muted-foreground">({t.role})</span></h2>
            <p className="mt-1 text-sm">
              <span className="text-muted-foreground">Coaches: </span>
              {t.coaches.length === 0 ? "—" : t.coaches.map((c) => `${c.name}${c.boksmart_number ? ` (BokSmart ${c.boksmart_number})` : " (no BokSmart no.)"}`).join(", ")}
            </p>
            <p className="mt-1 text-sm">
              <span className="text-muted-foreground">House doctor: </span>
              {t.house_doctor.name
                ? `${t.house_doctor.name}${t.house_doctor.practice ? `, ${t.house_doctor.practice}` : ""}${t.house_doctor.phone ? ` (${t.house_doctor.phone})` : ""}`
                : "—"}
            </p>
            <table className="mt-2 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">#</th>
                  <th className="py-1 pr-2 font-medium">Player</th>
                  <th className="py-1 font-medium">Front-row backup</th>
                </tr>
              </thead>
              <tbody>
                {t.squad.length === 0 ? (
                  <tr><td colSpan={3} className="py-1 text-muted-foreground">No squad selected.</td></tr>
                ) : (
                  t.squad.map((p) => (
                    <tr key={p.player_id} className="border-b border-border/50">
                      <td className="py-1 pr-2">{p.jersey_number}</td>
                      <td className="py-1 pr-2">{p.player_name}</td>
                      <td className="py-1">
                        {[p.backup_hooker && "hooker", p.backup_prop && "prop"].filter(Boolean).join(", ") || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}
